// Biome tilesets: 28 frames of 48×48 on an 8-column sheet (see TILE_FRAMES).
// Frames 0–15 are the solid autotile (frame = neighbour mask: 1 up, 2 right, 4 down,
// 8 left; a set bit means that neighbour is solid). Every interior pattern repeats
// with a 48 px period so neighbouring tiles join seamlessly.

import Phaser from 'phaser';
import { COLORS, mix } from '../../config/palette';
import { BIOME_IDS, type BiomeId } from '../../core/types';
import { hashSeed } from '../../core/rng';
import { TILE_FRAMES, TILESET_COLUMNS, TILESET_FRAME_COUNT, tilesetKey } from '../keys';
import { BIOME_TILE_STYLE, type BiomeTileStyle } from './biomes';
import {
  addGridFrames,
  fbm,
  hash2,
  modulate,
  neonStroke,
  newCanvas,
  noiseOctaves,
  PeriodicNoise,
  px,
  rgba,
  scratch,
  seeded,
  softDot,
  vGradient,
  type G,
} from './canvas';

const T = 48;

export function buildTilesets(scene: Phaser.Scene): void {
  for (const biome of BIOME_IDS) buildTileset(scene, biome);
}

function buildTileset(scene: Phaser.Scene, biome: BiomeId): void {
  const rows = Math.ceil(TILESET_FRAME_COUNT / TILESET_COLUMNS);
  const c = newCanvas(scene, tilesetKey(biome), TILESET_COLUMNS * T, rows * T);
  if (!c) return;
  const st = BIOME_TILE_STYLE[biome];
  const interior = interiorBase(biome, st);
  const back = backWallBase(biome, st);
  // Each frame is painted on its own 48×48 scratch canvas (pixel passes such as
  // modulate() read back absolute coordinates) and then blitted into the atlas.
  const frame = scratch(T, T);
  for (let f = 0; f < TILESET_FRAME_COUNT; f++) {
    frame.g.clearRect(0, 0, T, T);
    frame.g.save();
    drawFrame(frame.g, f, biome, st, interior, back);
    frame.g.restore();
    c.g.drawImage(frame.c, (f % TILESET_COLUMNS) * T, Math.floor(f / TILESET_COLUMNS) * T);
  }
  addGridFrames(c.tex, T, T, TILESET_FRAME_COUNT, TILESET_COLUMNS);
  // Nearest filtering: tiles sit at fractional camera offsets, and linear filtering
  // would bleed neighbouring atlas frames into tile edges (no extrusion margin).
  c.tex.setFilter(Phaser.Textures.FilterMode.NEAREST);
  c.tex.refresh();
}

function drawFrame(g: G, f: number, biome: BiomeId, st: BiomeTileStyle, interior: HTMLCanvasElement, back: HTMLCanvasElement): void {
  if (f < 16) {
    solidTile(g, f, biome, st, interior);
    return;
  }
  switch (f) {
    case TILE_FRAMES.solidDetailA:
      g.drawImage(interior, 0, 0);
      detailA(g, biome, st);
      break;
    case TILE_FRAMES.solidDetailB:
      g.drawImage(interior, 0, 0);
      detailB(g, biome, st);
      break;
    case TILE_FRAMES.platformLeft:
      platform(g, st, true, false);
      break;
    case TILE_FRAMES.platformMid:
      platform(g, st, false, false);
      break;
    case TILE_FRAMES.platformRight:
      platform(g, st, false, true);
      break;
    case TILE_FRAMES.platformSingle:
      platform(g, st, true, true);
      break;
    case TILE_FRAMES.hazard:
      hazard(g, biome, st, interior);
      break;
    case TILE_FRAMES.backWall:
      g.drawImage(back, 0, 0);
      break;
    case TILE_FRAMES.decorVent:
      g.drawImage(back, 0, 0);
      decorVent(g, st);
      break;
    case TILE_FRAMES.decorPipe:
      g.drawImage(back, 0, 0);
      decorPipe(g, biome, st);
      break;
    case TILE_FRAMES.decorGrate:
      g.drawImage(back, 0, 0);
      decorGrate(g, st);
      break;
    case TILE_FRAMES.decorStripes:
      g.drawImage(back, 0, 0);
      decorStripes(g, st);
      break;
  }
}

// ───────────────────────────── Interior plating ─────────────────────────────

/** Crisp 1-px line (Bresenham) — circuit traces stay sharp at nearest filtering. */
function pixelLine(g: G, x0: number, y0: number, x1: number, y1: number, color: number, alpha: number): void {
  g.fillStyle = rgba(color, alpha);
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    g.fillRect(x, y, 1, 1);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

function polyline(g: G, pts: number[], color: number, alpha: number): void {
  for (let i = 0; i < pts.length - 2; i += 2) pixelLine(g, pts[i], pts[i + 1], pts[i + 2], pts[i + 3], color, alpha);
}

/** Circuit trace: soft 3-px glow under a crisp 1-px line. */
function trace(g: G, pts: number[], color: number, alpha: number): void {
  g.save();
  g.strokeStyle = rgba(color, alpha * 0.22);
  g.lineWidth = 3;
  g.lineJoin = 'round';
  g.beginPath();
  g.moveTo(pts[0] + 0.5, pts[1] + 0.5);
  for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i] + 0.5, pts[i + 1] + 0.5);
  g.stroke();
  g.restore();
  polyline(g, pts, color, alpha);
}

function rivet(g: G, x: number, y: number, st: BiomeTileStyle): void {
  px(g, x, y, 2, 2, st.seam, 0.9);
  px(g, x, y, 1, 1, mix(st.trimMid, st.trimHi, 0.3), 0.9);
  px(g, x + 1, y + 2, 2, 1, st.seam, 0.5);
}

function interiorBase(biome: BiomeId, st: BiomeTileStyle): HTMLCanvasElement {
  const { c, g } = scratch(T, T);
  const seed = hashSeed('tile-interior', biome);
  px(g, 0, 0, T, T, st.base);
  const oct = noiseOctaves(seed, 12, T, T, 3);
  const blotch = new PeriodicNoise(seed + 99, 16, T, T);
  modulate(g, 0, 0, T, T, (x, y) => {
    const n = fbm(oct, x, y);
    const b = blotch.at(x, y);
    return 0.86 + 0.26 * n + (hash2(x, y, seed) - 0.5) * 0.07 - (b > 0.62 ? (b - 0.62) * 0.6 : 0);
  });
  const rng = seeded(`interior-${biome}`);

  if (biome === 'graveyard') {
    // Slab seams on the tile border.
    px(g, 0, 0, T, 1, st.seam);
    px(g, 0, 0, 1, T, st.seam);
    px(g, 1, 1, T - 1, 1, st.baseHi, 0.55);
    px(g, 1, 1, 1, T - 1, st.baseHi, 0.35);
    // Formwork line halfway down.
    px(g, 1, 24, T - 1, 1, st.seam, 0.35);
    // Wet streaks running down (wrap vertically so they continue into the tile below).
    for (let i = 0; i < 6; i++) {
      const x = rng.int(3, 45);
      const y0 = rng.int(0, 47);
      const len = rng.int(9, 26);
      const a = rng.float(0.1, 0.2);
      for (let k = 0; k < len; k++) {
        const y = (y0 + k) % T;
        px(g, x, y, 1, 1, 0x9fb6c8, a * (1 - k / len));
      }
    }
    // Anchor bolts.
    rivet(g, 6, 6, st);
    rivet(g, 40, 40, st);
  } else if (biome === 'foundry') {
    // 24 px riveted plates.
    for (const s of [0, 24]) {
      px(g, 0, s, T, 1, st.seam);
      px(g, s, 0, 1, T, st.seam);
      px(g, 0, s + 1, T, 1, st.baseHi, 0.45);
      px(g, s + 1, 0, 1, T, st.baseHi, 0.3);
    }
    for (const [ox, oy] of [
      [0, 0],
      [24, 24],
    ]) {
      rivet(g, ox + 3, oy + 3, st);
      rivet(g, ox + 19, oy + 3, st);
      rivet(g, ox + 3, oy + 19, st);
      rivet(g, ox + 19, oy + 19, st);
    }
    // Faint heat seam.
    px(g, 0, 24, T, 1, st.accent, 0.22);
    px(g, 0, 23, T, 1, st.accent, 0.06);
    px(g, 0, 25, T, 1, st.accent, 0.08);
    // Rust bloom.
    for (let i = 0; i < 3; i++) softDot(g, rng.int(8, 40), rng.int(8, 40), rng.int(6, 11), COLORS.rust, 0.18);
  } else {
    // Black alloy panels with circuit traces whose ports line up with neighbours.
    px(g, 0, 0, T, 1, st.seam);
    px(g, 0, 0, 1, T, st.seam);
    px(g, 1, 1, T - 1, 1, st.baseHi, 0.7);
    px(g, 1, 1, 1, T - 1, st.baseHi, 0.45);
    trace(g, [0, 14, 17, 14, 23, 8, 39, 8, 45, 14, 47, 14], st.accent, 0.42);
    trace(g, [32, 0, 32, 25, 37, 30, 37, 40, 32, 45, 32, 47], st.accent, 0.36);
    trace(g, [6, 30, 13, 37, 22, 37], st.accent2, 0.5);
    for (const [x, y, col] of [
      [22, 7, st.accent],
      [38, 7, st.accent],
      [5, 29, st.accent2],
      [21, 36, st.accent2],
      [36, 39, st.accent],
    ] as const) {
      px(g, x, y, 3, 3, col, 0.25);
      px(g, x + 1, y + 1, 1, 1, col, 0.9);
    }
  }
  return c;
}

// ───────────────────────────── Solid autotile ─────────────────────────────

const TRIM_H: Record<BiomeId, number> = { graveyard: 8, foundry: 8, vault: 7 };

function solidTile(g: G, mask: number, biome: BiomeId, st: BiomeTileStyle, interior: HTMLCanvasElement): void {
  const expT = (mask & 1) === 0;
  const expR = (mask & 2) === 0;
  const expB = (mask & 4) === 0;
  const expL = (mask & 8) === 0;
  g.drawImage(interior, 0, 0);
  const top = expT ? TRIM_H[biome] : 0;
  const bottom = expB ? T - 1 : T;

  if (expB) {
    // Underside: darkened lip and outline.
    vGradient(g, 0, 40, T, 8, [
      [0, st.outline, 0],
      [1, st.outline, 0.55],
    ]);
    px(g, 0, 41, T, 1, st.baseHi, 0.22);
    px(g, 0, 47, T, 1, st.outline);
  }
  if (expL) {
    px(g, 0, top, 1, bottom - top, st.outline);
    px(g, 1, top, 1, bottom - top, mix(st.trimMid, st.base, 0.35));
    px(g, 2, top, 1, bottom - top, mix(st.trimLo, st.base, 0.55));
  }
  if (expR) {
    px(g, T - 1, top, 1, bottom - top, st.outline);
    px(g, T - 2, top, 1, bottom - top, mix(st.base, st.outline, 0.5));
    px(g, T - 3, top, 1, bottom - top, mix(st.base, st.outline, 0.22));
  }
  if (expT) topTrim(g, mask, biome, st, expL, expR);
  // Rounded outer corners.
  if (expT && expL) g.clearRect(0, 0, 1, 1);
  if (expT && expR) g.clearRect(T - 1, 0, 1, 1);
  if (expB && expL) g.clearRect(0, T - 1, 1, 1);
  if (expB && expR) g.clearRect(T - 1, T - 1, 1, 1);
}

function topTrim(g: G, mask: number, biome: BiomeId, st: BiomeTileStyle, expL: boolean, expR: boolean): void {
  const h = TRIM_H[biome];
  const plate = h - 3; // rows of lit metal before the shadow + accent rows
  px(g, 0, 0, T, 1, st.trimHi);
  px(g, 0, 1, T, 1, mix(st.trimHi, st.trimMid, 0.45));
  vGradient(g, 0, 2, T, plate - 2, [
    [0, st.trimMid],
    [1, st.trimLo],
  ]);
  px(g, 0, plate, T, 1, st.outline, 0.9);
  const ay = plate + 1;
  px(g, 0, ay, T, 1, st.accent, st.accentAlpha);
  px(g, 0, ay + 1, T, 1, st.accent, st.accentAlpha * 0.25);

  if (biome === 'graveyard') {
    // Wet sheen speckles on the lip.
    for (let x = 0; x < T; x++) {
      if (hash2(x, mask, 7) > 0.8) px(g, x, 1 + (x % 3), 1, 1, 0xdbe8f5, 0.35);
    }
    // Faded / broken neon: some tiles lose a segment, others carry a pink marker LED.
    if ((mask * 7) % 5 === 0) g.clearRect(26, ay, 9, 2);
    if ((mask * 7) % 5 === 0) px(g, 26, ay, 9, 1, st.outline, 0.85);
    if (mask % 4 === 2) {
      px(g, 39, ay, 5, 1, st.accent2, 0.9);
      px(g, 38, ay - 1, 7, 3, st.accent2, 0.15);
    }
  } else if (biome === 'foundry') {
    for (const x of [5, 17, 29, 41]) rivet(g, x, 2, st);
    px(g, 0, ay + 2, T, 1, st.accent, 0.1);
  } else {
    // Smooth alloy: a fine specular line and violet nodes on the cyan strip.
    px(g, 0, 2, T, 1, st.trimHi, 0.25);
    px(g, 10, ay, 3, 1, st.accent2, 0.95);
    px(g, 34, ay, 3, 1, st.accent2, 0.95);
  }

  // End caps: outline + bevel on exposed sides.
  if (expL) {
    px(g, 0, 0, 1, h, st.outline);
    px(g, 1, 1, 1, plate - 1, st.trimHi, 0.55);
  }
  if (expR) {
    px(g, T - 1, 0, 1, h, st.outline);
    px(g, T - 2, 1, 1, plate - 1, st.trimLo);
  }
}

function detailA(g: G, biome: BiomeId, st: BiomeTileStyle): void {
  if (biome === 'graveyard') {
    // Bolted access hatch with a faint status light.
    px(g, 11, 12, 26, 22, st.seam, 0.9);
    px(g, 12, 13, 24, 20, mix(st.base, st.baseHi, 0.5));
    px(g, 12, 13, 24, 1, st.trimMid, 0.6);
    px(g, 12, 13, 1, 20, st.trimMid, 0.35);
    px(g, 13, 31, 23, 1, st.seam, 0.7);
    for (const [x, y] of [
      [14, 15],
      [32, 15],
      [14, 28],
      [32, 28],
    ])
      rivet(g, x, y, st);
    for (let y = 18; y < 28; y += 3) px(g, 18, y, 12, 1, st.seam, 0.75);
    px(g, 31, 22, 2, 2, st.accent, 0.9);
    softDot(g, 32, 23, 4, st.accent, 0.25);
  } else if (biome === 'foundry') {
    // Heat vent with glowing slats.
    px(g, 9, 13, 30, 20, st.seam);
    vGradient(g, 10, 14, 28, 18, [
      [0, st.accent, 0.12],
      [1, st.accent, 0.45],
    ]);
    for (let y = 15; y < 32; y += 4) {
      px(g, 10, y, 28, 2, st.trimLo);
      px(g, 10, y, 28, 1, st.trimMid, 0.8);
    }
    rivet(g, 6, 10, st);
    rivet(g, 40, 10, st);
    rivet(g, 6, 34, st);
    rivet(g, 40, 34, st);
  } else {
    // Circuit node: chip with a glowing core.
    px(g, 15, 15, 18, 18, st.seam);
    px(g, 16, 16, 16, 16, mix(st.base, st.baseHi, 0.6));
    for (let i = 0; i < 4; i++) {
      px(g, 18 + i * 4, 13, 2, 2, st.trimMid, 0.8);
      px(g, 18 + i * 4, 33, 2, 2, st.trimMid, 0.8);
      px(g, 13, 18 + i * 4, 2, 2, st.trimMid, 0.8);
      px(g, 33, 18 + i * 4, 2, 2, st.trimMid, 0.8);
    }
    softDot(g, 24, 24, 9, st.accent, 0.35);
    px(g, 22, 22, 4, 4, st.accent, 0.95);
    px(g, 23, 23, 2, 2, 0xffffff, 0.9);
  }
}

function detailB(g: G, biome: BiomeId, st: BiomeTileStyle): void {
  if (biome === 'graveyard') {
    // Cracked concrete with a water stain.
    softDot(g, 24, 30, 14, 0x0a0e14, 0.35);
    polyline(g, [10, 9, 16, 15, 15, 21, 22, 27, 21, 34, 27, 40], st.seam, 0.95);
    polyline(g, [16, 15, 22, 14, 27, 18], st.seam, 0.8);
    polyline(g, [11, 9, 17, 15, 16, 21, 23, 27], st.baseHi, 0.35);
    for (let k = 0; k < 10; k++) px(g, 31, 22 + k, 1, 1, 0x9fb6c8, 0.18 * (1 - k / 10));
  } else if (biome === 'foundry') {
    // Glowing heat cracks.
    const crack = [6, 30, 13, 26, 19, 29, 26, 22, 33, 24, 41, 18];
    g.save();
    g.beginPath();
    g.moveTo(crack[0], crack[1]);
    for (let i = 2; i < crack.length; i += 2) g.lineTo(crack[i], crack[i + 1]);
    neonStroke(g, st.accent, 1, 2.5, 0.85, 0xfff0c0, 0.5);
    g.restore();
  } else {
    // Violet conduit with energy nodes.
    px(g, 22, 6, 4, 36, st.seam);
    px(g, 23, 6, 2, 36, st.accent2, 0.35);
    px(g, 23, 6, 1, 36, st.accent2, 0.75);
    softDot(g, 24, 15, 6, st.accent2, 0.35);
    softDot(g, 24, 33, 6, st.accent2, 0.35);
    px(g, 21, 14, 6, 3, st.trimMid);
    px(g, 21, 32, 6, 3, st.trimMid);
  }
}

// ───────────────────────────── One-way platforms ─────────────────────────────

function platform(g: G, st: BiomeTileStyle, capL: boolean, capR: boolean): void {
  // Deck: lit rail (rows 0–3), see-through grating (5–10), bottom rail (11–13).
  px(g, 0, 0, T, 1, st.trimHi);
  px(g, 0, 1, T, 1, mix(st.trimHi, st.trimMid, 0.5));
  px(g, 0, 2, T, 2, st.trimMid);
  px(g, 0, 4, T, 1, st.outline);
  px(g, 0, 5, T, 6, st.outline, 0.5);
  for (let x = 2; x < T; x += 6) {
    px(g, x, 5, 2, 6, st.trimLo);
    px(g, x, 5, 1, 6, mix(st.trimLo, st.trimMid, 0.6));
  }
  px(g, 0, 7, T, 1, st.trimLo, 0.9);
  px(g, 0, 11, T, 1, st.trimMid);
  px(g, 0, 12, T, 1, st.trimLo);
  px(g, 0, 13, T, 1, st.outline);
  for (const x of [11, 35]) {
    px(g, x, 12, 3, 1, st.accent, 0.95);
    px(g, x - 1, 11, 5, 3, st.accent, 0.18);
  }
  if (capL) platformCap(g, st, false);
  if (capR) platformCap(g, st, true);
}

function platformCap(g: G, st: BiomeTileStyle, right: boolean): void {
  g.save();
  if (right) {
    g.translate(T, 0);
    g.scale(-1, 1);
  }
  // End plate.
  px(g, 0, 0, 7, 14, st.trimMid);
  px(g, 0, 0, 7, 1, st.trimHi);
  px(g, 0, 0, 1, 14, st.outline);
  px(g, 1, 1, 1, 12, mix(st.trimHi, st.trimMid, 0.4));
  px(g, 6, 1, 1, 13, st.trimLo);
  px(g, 0, 13, 7, 1, st.outline);
  px(g, 3, 6, 2, 2, st.seam);
  px(g, 3, 6, 1, 1, st.trimHi, 0.8);
  // Wall bracket under the cap.
  g.beginPath();
  g.moveTo(1, 14);
  g.lineTo(14, 14);
  g.lineTo(1, 28);
  g.closePath();
  g.fillStyle = rgba(st.outline);
  g.fill();
  g.beginPath();
  g.moveTo(2, 14);
  g.lineTo(11.5, 14);
  g.lineTo(2, 24.5);
  g.closePath();
  g.fillStyle = rgba(st.trimLo);
  g.fill();
  px(g, 2, 14, 1, 10, mix(st.trimLo, st.trimMid, 0.6));
  g.restore();
}

// ───────────────────────────── Hazards ─────────────────────────────

/** Diagonal warning stripes, period 12 px (tiles along x). */
function stripes(g: G, y: number, h: number, a: number, b: number, alpha = 1): void {
  g.save();
  g.beginPath();
  g.rect(0, y, T, h);
  g.clip();
  px(g, 0, y, T, h, b, alpha);
  g.fillStyle = rgba(a, alpha);
  for (let x = -h - 12; x < T + h; x += 12) {
    g.beginPath();
    g.moveTo(x, y + h);
    g.lineTo(x + 6, y + h);
    g.lineTo(x + 6 + h, y);
    g.lineTo(x + h, y);
    g.closePath();
    g.fill();
  }
  g.restore();
}

function hazard(g: G, biome: BiomeId, st: BiomeTileStyle, interior: HTMLCanvasElement): void {
  g.drawImage(interior, 0, 0);
  if (biome === 'graveyard') electricGrid(g, st);
  else if (biome === 'foundry') moltenGrate(g, st);
  else voidRift(g, st);
}

function electricGrid(g: G, st: BiomeTileStyle): void {
  const hot = COLORS.threat;
  px(g, 0, 0, T, 1, st.trimHi);
  px(g, 0, 1, T, 1, st.trimMid);
  // Recessed channel with a live field.
  px(g, 0, 2, T, 15, st.outline);
  vGradient(g, 0, 3, T, 13, [
    [0, hot, 0.12],
    [0.5, hot, 0.5],
    [1, hot, 0.12],
  ]);
  for (let x = 3; x < T; x += 8) {
    px(g, x, 2, 2, 15, st.trimLo);
    px(g, x, 2, 1, 15, st.trimMid, 0.7);
  }
  px(g, 0, 9, T, 1, st.trimLo, 0.85);
  // Arcs (start and end at the same height so the strip tiles).
  const arcs = [
    [6, 3, 10, 5, 11, 4, 8, 4, 6],
    [12, 15, 10, 14, 9, 15, 11, 14, 12],
  ];
  for (const ys of arcs) {
    g.beginPath();
    ys.forEach((y, i) => (i === 0 ? g.moveTo(0, y) : g.lineTo((i / (ys.length - 1)) * T, y)));
    neonStroke(g, hot, 1, 2.2, 1, 0xffffff, 0.75);
  }
  // Lip + warning stripes on the housing.
  px(g, 0, 17, T, 1, st.trimMid);
  px(g, 0, 18, T, 1, st.trimLo);
  px(g, 0, 19, T, 1, st.outline);
  stripes(g, 21, 7, 0xc8a02a, 0x15181e, 0.85);
  px(g, 0, 28, T, 1, st.outline, 0.8);
  px(g, 11, 33, 2, 2, hot, 0.95);
  px(g, 35, 33, 2, 2, hot, 0.95);
  softDot(g, 12, 34, 4, hot, 0.3);
  softDot(g, 36, 34, 4, hot, 0.3);
}

function moltenGrate(g: G, st: BiomeTileStyle): void {
  // Molten pool under an iron grate; heat bleeds into the housing.
  vGradient(g, 0, 1, T, 20, [
    [0, 0xfff1b0],
    [0.16, 0xffb43c],
    [0.5, 0xff6a14],
    [1, 0x7a1e08],
  ]);
  const crust = new PeriodicNoise(hashSeed('molten'), 8, T, 24);
  modulate(g, 0, 1, T, 20, (x, y) => {
    const n = crust.at(x, y * 1.6);
    return n > 0.68 ? 0.45 : 1 + (n - 0.5) * 0.25;
  });
  for (const [bx, by, r] of [
    [9, 8, 2.2],
    [30, 13, 1.8],
    [41, 6, 1.6],
    [20, 16, 1.4],
  ]) {
    softDot(g, bx, by, r * 2.4, 0xfff6c8, 0.55);
  }
  px(g, 0, 0, T, 2, st.trimLo);
  px(g, 0, 0, T, 1, st.trimMid);
  for (let x = 4; x < T; x += 8) {
    px(g, x, 1, 3, 20, st.outline);
    px(g, x, 1, 1, 20, st.trimLo);
    px(g, x, 1, 3, 1, st.trimMid);
  }
  px(g, 0, 21, T, 1, st.trimMid);
  px(g, 0, 22, T, 2, st.outline);
  vGradient(g, 0, 24, T, 14, [
    [0, st.accent, 0.35],
    [1, st.accent, 0],
  ]);
  rivet(g, 8, 28, st);
  rivet(g, 32, 28, st);
}

function voidRift(g: G, st: BiomeTileStyle): void {
  const edgeA = new PeriodicNoise(hashSeed('rift-a'), 6, T, 6);
  const edgeB = new PeriodicNoise(hashSeed('rift-b'), 6, T, 6);
  px(g, 0, 0, T, 1, st.trimHi);
  px(g, 0, 1, T, 1, st.trimMid);
  px(g, 0, 2, T, 1, st.outline);
  const danger = 0xff3b8c;
  for (let x = 0; x < T; x++) {
    const top = Math.round(4 + edgeA.at(x, 0) * 4);
    const bot = Math.round(15 + edgeB.at(x, 0) * 4);
    // Void body.
    for (let y = top; y <= bot; y++) {
      const t = (y - top) / Math.max(1, bot - top);
      px(g, x, y, 1, 1, mix(0x12062a, 0x030108, Math.sin(t * Math.PI)), 1);
    }
    // Hot rims.
    px(g, x, top - 1, 1, 1, danger, 0.45);
    px(g, x, top, 1, 1, st.accent2, 1);
    px(g, x, top + 1, 1, 1, st.accent2, 0.35);
    px(g, x, bot, 1, 1, st.accent2, 0.9);
    px(g, x, bot + 1, 1, 1, danger, 0.45);
  }
  // Filaments and sparkles inside the tear.
  g.save();
  g.beginPath();
  g.moveTo(0, 11);
  g.bezierCurveTo(12, 8, 20, 14, 30, 10);
  g.bezierCurveTo(38, 7, 44, 12, 48, 11);
  neonStroke(g, st.accent2, 0.6, 1.4, 0.55, 0xffffff, 0.3);
  g.restore();
  for (const [x, y] of [
    [7, 9],
    [19, 13],
    [33, 11],
    [43, 9],
  ])
    px(g, x, y, 1, 1, 0xffffff, 0.9);
  // Housing below with conduits feeding the rift.
  px(g, 0, 22, T, 1, st.trimLo);
  trace(g, [12, 23, 12, 34, 18, 40], st.accent2, 0.5);
  trace(g, [36, 23, 36, 30, 30, 36], st.accent2, 0.5);
}

// ───────────────────────────── Back wall + decor ─────────────────────────────

function backWallBase(biome: BiomeId, st: BiomeTileStyle): HTMLCanvasElement {
  const { c, g } = scratch(T, T);
  const seed = hashSeed('tile-back', biome);
  px(g, 0, 0, T, T, st.back);
  const oct = noiseOctaves(seed, 12, T, T, 3);
  modulate(g, 0, 0, T, T, (x, y) => 0.88 + 0.22 * fbm(oct, x, y) + (hash2(x, y, seed) - 0.5) * 0.05);
  const rng = seeded(`back-${biome}`);
  if (biome === 'graveyard') {
    px(g, 0, 0, T, 1, st.backSeam);
    px(g, 0, 0, 1, T, st.backSeam);
    px(g, 24, 0, 1, T, st.backSeam, 0.6);
    px(g, 1, 1, T - 1, 1, st.backHi, 0.5);
    for (let i = 0; i < 4; i++) {
      const x = rng.int(2, 46);
      const y0 = rng.int(0, 47);
      const len = rng.int(10, 30);
      for (let k = 0; k < len; k++) px(g, x, (y0 + k) % T, 1, 1, 0x000000, 0.25 * (1 - k / len));
    }
    px(g, 0, 36, T, 1, st.accent, 0.06);
  } else if (biome === 'foundry') {
    // Corrugated sheet: ridges every 8 px.
    for (let x = 0; x < T; x += 8) {
      px(g, x, 0, 2, T, st.backHi, 0.65);
      px(g, x + 2, 0, 3, T, st.back, 0.4);
      px(g, x + 5, 0, 2, T, st.backSeam, 0.55);
    }
    px(g, 0, 0, T, 1, st.backSeam);
    px(g, 0, 1, T, 1, st.backHi, 0.5);
    for (const x of [4, 20, 36]) px(g, x, 3, 2, 2, st.trimLo, 0.8);
    for (let i = 0; i < 2; i++) softDot(g, rng.int(8, 40), rng.int(10, 40), rng.int(8, 14), COLORS.rust, 0.14);
  } else {
    // Offset alloy bricks (24×12) with faint joints.
    for (let row = 0; row < 4; row++) {
      const y = row * 12;
      const off = row % 2 ? 12 : 0;
      px(g, 0, y, T, 1, st.backSeam);
      px(g, 0, y + 1, T, 1, st.backHi, 0.55);
      for (let x = off; x < T + 24; x += 24) {
        px(g, x % T, y, 1, 12, st.backSeam);
      }
    }
    px(g, 12, 12, 1, 1, st.accent2, 0.6);
    px(g, 36, 36, 1, 1, st.accent, 0.5);
  }
  return c;
}

function decorVent(g: G, st: BiomeTileStyle): void {
  px(g, 7, 9, 34, 30, st.outline);
  px(g, 8, 10, 32, 28, st.backSeam);
  vGradient(g, 9, 11, 30, 26, [
    [0, st.accent, 0.04],
    [1, st.accent, 0.2],
  ]);
  for (let y = 12; y < 37; y += 4) {
    px(g, 9, y, 30, 2, st.trimLo);
    px(g, 9, y, 30, 1, st.trimMid, 0.7);
  }
  px(g, 7, 9, 34, 1, st.trimMid, 0.7);
  for (const [x, y] of [
    [9, 5],
    [37, 5],
    [9, 41],
    [37, 41],
  ]) {
    px(g, x, y, 2, 2, st.trimLo);
    px(g, x, y, 1, 1, st.trimMid);
  }
}

function decorPipe(g: G, biome: BiomeId, st: BiomeTileStyle): void {
  const base = biome === 'foundry' ? 0x7a4e36 : st.metal;
  // Cylinder shading top → bottom.
  vGradient(g, 0, 17, T, 14, [
    [0, mix(base, 0x000000, 0.55)],
    [0.18, mix(base, 0xffffff, 0.35)],
    [0.4, base],
    [1, mix(base, 0x000000, 0.65)],
  ]);
  px(g, 0, 16, T, 1, st.outline);
  px(g, 0, 31, T, 1, st.outline);
  if (biome === 'vault') {
    px(g, 0, 23, T, 2, st.accent, 0.55);
    px(g, 0, 22, T, 4, st.accent, 0.12);
  }
  // Flanges at both ends (pipes join flange-to-flange).
  for (const x of [0, T - 4]) {
    vGradient(g, x, 13, 4, 22, [
      [0, mix(base, 0x000000, 0.4)],
      [0.2, mix(base, 0xffffff, 0.4)],
      [1, mix(base, 0x000000, 0.6)],
    ]);
    px(g, x + 1, 15, 2, 1, st.outline, 0.8);
    px(g, x + 1, 32, 2, 1, st.outline, 0.8);
  }
  // Wall strap.
  px(g, 22, 15, 4, 18, st.outline);
  px(g, 23, 15, 2, 18, st.trimLo);
  px(g, 21, 33, 6, 3, st.trimLo);
}

function decorGrate(g: G, st: BiomeTileStyle): void {
  px(g, 4, 4, 40, 40, st.outline);
  px(g, 5, 5, 38, 38, st.backSeam);
  vGradient(g, 5, 5, 38, 38, [
    [0.5, st.accent, 0],
    [1, st.accent, 0.14],
  ]);
  g.save();
  g.beginPath();
  g.rect(5, 5, 38, 38);
  g.clip();
  g.strokeStyle = rgba(st.trimLo);
  g.lineWidth = 1.5;
  for (let k = -40; k < 48; k += 6) {
    g.beginPath();
    g.moveTo(k, 4);
    g.lineTo(k + 40, 44);
    g.stroke();
    g.beginPath();
    g.moveTo(k + 40, 4);
    g.lineTo(k, 44);
    g.stroke();
  }
  g.restore();
  px(g, 4, 4, 40, 1, st.trimMid, 0.8);
  px(g, 4, 4, 1, 40, st.trimMid, 0.5);
}

function decorStripes(g: G, st: BiomeTileStyle): void {
  px(g, 0, 15, T, 1, st.trimMid);
  px(g, 0, 16, T, 1, st.outline);
  stripes(g, 17, 14, 0xb08a2a, 0x121418, 0.92);
  px(g, 0, 31, T, 1, st.outline);
  px(g, 0, 32, T, 1, st.trimLo);
  // Grime over the paint.
  modulate(g, 0, 17, T, 14, (x, y) => 0.8 + 0.25 * hash2(x >> 1, (y + 17) >> 1, 11));
}
